import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { Collapse, Grid } from "antd";
import LoadingContent from "../../common/LoadingContent";
import PageTitleNoAdd from "../../components/header/PageTitleNoAdd";
import axiosWithAuth from "../../utils/axiosWithAuth";

import OrderTaxRows from "../../common/OrderTaxRows";
import OrderTipRow from "../../common/OrderTipRow";

import useOrderTaxSummary from "../../hooks/useOrderTaxSummary";

export default function OrderConfirmationPageRestaurant() {
  const { id } = useParams();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Panel } = Collapse;

  const [orderData, setOrderData] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const taxSummary = useOrderTaxSummary(orderData);

  useEffect(() => {
    const APIURL = `/api/restaurant/order/getOrdersDetails/${id}`;

    axiosWithAuth()
      .get(APIURL)
      .then((res) => {
        setOrderData(res.data.data.order);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
      });
  }, [id]);

  return (
    <div id="order-confirmation">
      <Helmet>
        <title>BIZZLL - Order Confirmation</title>
      </Helmet>

      <PageTitleNoAdd pageTitle="Order Confirmation" />

      <div className="page-wrapper">
        {loading ? (
          <LoadingContent />
        ) : (
          <div className="inner-container">
            <div className="header">
              <h2>Thank you for your order!</h2>

              <h3>Order Number: {orderData.id}</h3>
            </div>

            <div className="order-details">
              <h3>Order Details</h3>

              {screens.lg ? (
                <div className="products-table">
                  <div className="row labels">
                    <div className="item">Item</div>
                    <div className="price">Price</div>
                    <div className="quantity">Quantity</div>
                    <div className="total">Total</div>
                  </div>

                  {orderData.detail.map((item) => {
                    const productName = item.dish_name || "Unknown";
                    const extras = item.extras?.length
                      ? item.extras.map((extra) => extra.name).join(", ")
                      : "No extras";

                    // Calcular el costo total de los extras
                    const extrasTotal =
                      item.extras?.reduce(
                        (sum, extra) => sum + (extra.price || 0),
                        0
                      ) || 0;

                    // Calcular el total incluyendo los extras
                    const totalPrice =
                      item.dish_price * item.dish_quantity + extrasTotal;

                    return (
                      <div className="row items" key={item.id}>
                        <div className="item">
                          {productName}
                          <span>Addons: {extras}</span>
                        </div>
                        <div className="price">
                          ${item.dish_price.toFixed(2)}
                        </div>
                        <div className="quantity">{item.dish_quantity}</div>
                        <div className="total">${totalPrice.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Collapse accordion>
                  {orderData.detail.map((item) => {
                    const productName = item.dish_name || "Unknown";
                    const extras = item.extras?.length
                      ? item.extras.map((extra) => extra.name).join(", ")
                      : "No extras";

                    // Calcular el costo total de los extras
                    const extrasTotal =
                      item.extras?.reduce(
                        (sum, extra) => sum + (extra.price || 0),
                        0
                      ) || 0;

                    // Calcular el total incluyendo los extras
                    const totalPrice =
                      item.dish_price * item.dish_quantity + extrasTotal;

                    return (
                      <Panel
                        key={item.id}
                        header={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              <strong>{productName}</strong>
                            </span>
                            <span style={{ fontSize: "14px", color: "#666" }}>
                              ${totalPrice.toFixed(2)}
                            </span>
                          </div>
                        }
                      >
                        <p>
                          <strong>Item:</strong> {productName}
                        </p>
                        <p>
                          <strong>Addons:</strong> {extras}
                        </p>
                        <p>
                          <strong>Price:</strong> ${item.dish_price.toFixed(2)}
                        </p>
                        <p>
                          <strong>Quantity:</strong> {item.dish_quantity}
                        </p>
                        <p>
                          <strong>Total:</strong> ${totalPrice.toFixed(2)}
                        </p>
                      </Panel>
                    );
                  })}
                </Collapse>
              )}

              <div
                className="order-summary"
                style={{
                  marginTop: "30px",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <div
                  className="summary-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "12px 0",
                    borderBottom: "1px solid #e0e0e0",
                    fontSize: "16px",
                  }}
                >
                  <span className="label" style={{ fontWeight: "500" }}>
                    Delivery Price:
                  </span>
                  <span
                    className="value"
                    style={{ fontWeight: "600", color: "#333" }}
                  >
                    ${orderData.delivery_price}
                  </span>
                </div>
                <OrderTaxRows summary={taxSummary} />
                <OrderTipRow tip={taxSummary.tip} />
                <div
                  className="summary-row total-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "15px 0 5px 0",
                    fontSize: "20px",
                    fontWeight: "bold",
                  }}
                >
                  <span className="label" style={{ color: "#c63627" }}>
                    Total:
                  </span>
                  <span className="value" style={{ color: "#c63627" }}>
                    ${orderData.total_price_order}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {error !== "" && (
          <div className="inner-container">
            <div className="header">
              <h2>There was an error retrieving order</h2>

              <h3>{error}</h3>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
